import { QRCodeSVG } from 'qrcode.react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, resolveImage, hexToRgba, resolveLinkProps, isColorDark } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionDivider from './SectionDivider';

export default function DownloadSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'card';
    const accent = config.accent_color || primaryColor;
    const previewImageUrl = resolveImage(content, 'preview_image', mediaFolders) || content.preview_image_url;

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;

    const sectionButtons = content.buttons && content.buttons.length > 0
        ? content.buttons
        : content.cta_text || content.file_url ? [{ text: content.cta_text || 'Descargar brochure', link: content.file_url || '#', style: 'solid', icon: 'download', bg_color: '', text_color: '' }] : [];

    const DownloadButtons = () => sectionButtons.length > 0 ? (
        <div className="flex flex-wrap gap-3">
            {sectionButtons.map((btn, i) => {
                const btnStyle = btn.style || 'solid';
                const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                const lp = btn.link && (btn.link.startsWith('http') || btn.link.startsWith('/'))
                    ? { href: btn.link, target: '_blank', rel: 'noopener noreferrer' }
                    : resolveLinkProps(btn.link || '#');
                const customBg = btn.bg_color;
                const customText = btn.text_color;
                let cls = 'px-8 py-3.5 font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                let style = {};
                cls += btnStyle === 'pill' ? 'rounded-full ' : 'rounded-lg ';
                if (btnStyle === 'solid' || btnStyle === 'pill') {
                    if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#ffffff' : '#111827'); cls += 'shadow-lg hover:shadow-xl hover:opacity-90'; }
                    else { style.backgroundColor = accent; style.color = '#ffffff'; cls += 'shadow-lg hover:shadow-xl hover:opacity-90'; }
                } else if (btnStyle === 'outline') {
                    if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2 hover:opacity-80'; }
                    else { style.color = accent; style.borderColor = accent; cls += 'border-2 hover:opacity-80'; }
                } else { style.color = customText || s.text; cls += 'hover:opacity-80'; }
                if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
            })}
        </div>
    ) : null;

    const QrBlock = () => content.qr_url ? (
        <div className="flex flex-col items-center gap-2 mt-6">
            <QRCodeSVG value={content.qr_url} size={120} level="M" fgColor={accent} bgColor="transparent" />
            <span className="text-xs" style={{ color: s.textSub }}>Escanea para descargar</span>
        </div>
    ) : null;

    // Split variant
    if (variant === 'split') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Image */}
                        <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                            {previewImageUrl ? (
                                <img src={previewImageUrl} alt={content.title || 'Brochure'}
                                    className="rounded-2xl shadow-2xl w-full max-w-md mx-auto" />
                            ) : (
                                <div className="rounded-2xl w-full max-w-md mx-auto aspect-[3/4] flex items-center justify-center" style={{ backgroundColor: hexToRgba(accent, 0.06) }}>
                                    <svg className="w-20 h-20" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            )}
                        </div>
                        {/* Text */}
                        <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                            {content.title && <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: s.text }}>{content.title}</h2>}
                            {content.subtitle && <p className="text-lg leading-relaxed mb-8" style={{ color: s.textSub }}>{content.subtitle}</p>}
                            <DownloadButtons />
                            <QrBlock />
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // Banner variant
    if (variant === 'banner') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6 rounded-2xl p-8" style={{ backgroundColor: hexToRgba(accent, 0.06) }}>
                        <div className="flex items-center gap-5">
                            {previewImageUrl ? (
                                <img src={previewImageUrl} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />
                            ) : (
                                <div className="w-20 h-20 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(accent, 0.13) }}>
                                    <svg className="w-10 h-10" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            )}
                            <div>
                                {content.title && <h3 className="text-xl font-bold" style={{ color: s.text }}>{content.title}</h3>}
                                {content.subtitle && <p className="text-sm mt-1" style={{ color: s.textSub }}>{content.subtitle}</p>}
                            </div>
                        </div>
                        <DownloadButtons />
                        <QrBlock />
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // Card variant (default) — centered card
    return (
        <section className={sectionCls} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div className="max-w-lg mx-auto text-center">
                    {previewImageUrl ? (
                        <img src={previewImageUrl} alt={content.title || 'Preview'}
                            className="rounded-2xl shadow-xl w-full max-w-xs mx-auto mb-8" />
                    ) : (
                        <div className="rounded-2xl w-32 h-40 mx-auto mb-8 flex items-center justify-center" style={{ backgroundColor: hexToRgba(accent, 0.06) }}>
                            <svg className="w-14 h-14" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                        </div>
                    )}
                    {content.title && <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: s.text }}>{content.title}</h2>}
                    {content.subtitle && <p className="text-sm leading-relaxed mb-8" style={{ color: s.textSub }}>{content.subtitle}</p>}
                    <DownloadButtons />
                    <QrBlock />
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
        </section>
    );
}
